import {
  Component,
  Input,
  OnInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { IDeviceShadowRecord } from 'src/app/shared/services/device-shadow/device-shadow.service';
import {
  DeviceViewService,
  AuxDissociationRequest,
} from 'src/app/shared/services/device-view/device-view.service';
import { SettingsService } from 'src/app/core/settings/settings.service';
import { AdminDeviceService } from 'src/app/shared/services/admin-device/admin-device.service';
import { SessionService } from 'src/app/auth/session/session.service';
import { IDevicePhoto } from '../services/monitoring.service';
import { environment } from 'src/environments/environment';
import {
  LiveImageService,
  LiveImageResponse,
} from 'src/app/shared/services/live-image/live-image.service';

interface AuxCameraView {
  name: string;
  label: string;
  photo: IDevicePhoto | undefined;
  selected: boolean;
  lastRecordedDate: string;
  photoSavedDate: string;
}

// Result the modal closes with. Hub dissociation is a short, awaited backend call
// (an admin device-API association clear for LX1 hubs, or a Unisyn settings push
// for AVM hubs); AUX dissociation is a long-running backend op, so it returns the
// affected view names for the parent to reconcile asynchronously.
export type HubAuxModalResult =
  | 'hub-dissociated'
  | { type: 'aux-dissociated'; views: string[] };

@Component({
  selector: 'dcc-hub-aux-modal',
  templateUrl: './hub-aux-modal.component.html',
  styleUrls: ['./hub-aux-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HubAuxModalComponent implements OnInit {
  @Input() metadata: IDeviceShadowRecord;
  @Input() photos: IDevicePhoto[];
  @Input() hubDissociationPending = false;

  auxCameras: AuxCameraView[] = [];
  selectHubDevice = false;
  isProcessing = false;
  canDissociate = false;
  requestingImages = false;
  liveImageError: string | null = null;
  private static readonly REQUEST_FAILED_MESSAGE =
    'Unable to retrieve image. Please try again.';

  private static readonly ONE_DAY_MS = 24 * 60 * 60 * 1000;
  private static readonly DISSOCIABLE_STATES = [
    'Active',
    'Awake',
    'Awake (Countdown)',
  ];
  private static readonly OFFLINE_STATES = ['Offline', 'Deep Sleep'];

  constructor(
    public activeModal: NgbActiveModal,
    private deviceViewService: DeviceViewService,
    private settingsService: SettingsService,
    private adminDeviceService: AdminDeviceService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private sessionService: SessionService,
    private liveImageService: LiveImageService,
  ) {}

  ngOnInit(): void {
    this.checkPermissions();
    this.buildAuxCameraList();
    void this.loadAuxImages();
  }

  // On open, pull the hub's cached aux images from Redis via the live-image API (no new capture).
  private async loadAuxImages(): Promise<void> {
    const views = this.auxCameras.map((c) => c.name);
    if (!this.deviceSerial || !views.length) return;
    try {
      const result = await this.liveImageService.getLiveImages(
        this.deviceSerial,
        views,
        false,
        this.isLytxOneHub,
      );
      if (result.status === 'COMPLETE') {
        this.applyLiveImages(result.images);
      }
    } catch {
      // Keep the cards built from the inputs.
    }
    this.cdr.markForCheck();
  }

  get hubSerial(): string {
    return this.metadata?.followerSerial || '';
  }

  get deviceSerial(): string {
    return this.metadata?.serialNumber || '';
  }

  get devicePlatform(): string {
    return this.metadata?.platform || '';
  }

  get operationalState(): string {
    return this.metadata?.operationalState || '';
  }

  get vehicleInfo(): string {
    const name = this.metadata?.vehicleName || '';
    const group = this.metadata?.groupName || '';
    if (name && group) {
      return `${name} (${group})`;
    }
    return name || group || '';
  }

  get lastCommunicated(): string {
    return this.metadata?.lastCommunicationDate || '';
  }

  get hubLastCommunicated(): string {
    return this.metadata?.hubLastCommunicationDate || '';
  }

  get selectedCount(): number {
    if (this.selectHubDevice) return 1;
    return this.auxCameras.filter((c) => c.selected).length;
  }

  get hasSelection(): boolean {
    return this.selectHubDevice || this.auxCameras.some((c) => c.selected);
  }

  get disassociateButtonLabel(): string {
    if (this.selectHubDevice) return 'Disassociate Device';
    const auxCount = this.auxCameras.filter((c) => c.selected).length;
    return auxCount > 0 ? `Disassociate Aux (${auxCount})` : 'Disassociate Device';
  }

  get isDissociableState(): boolean {
    const state = this.metadata?.operationalState ?? '';
    return HubAuxModalComponent.DISSOCIABLE_STATES.includes(state);
  }

  get isDisassociateDisabled(): boolean {
    if (!this.canDissociate || !this.hasSelection || this.isProcessing || this.hubDissociationPending)
      return true;
    // Hub selection requires the device to be in a dissociable operational state.
    if (this.selectHubDevice && !this.isDissociableState) return true;
    return false;
  }

  readonly noAccessMessage = "You don't have access to perform this action";

  get hubDisabledReason(): string {
    if (!this.canDissociate) return this.noAccessMessage;
    if (!this.isDissociableState) {
      return 'Device must be Active, Awake, or Awake (Countdown) to dissociate HUB';
    }
    return '';
  }

  get auxDisabledReason(): string {
    return this.canDissociate ? '' : this.noAccessMessage;
  }

  onHubCheckChange(checked: boolean): void {
    if (
      this.hubDissociationPending ||
      !this.canDissociate ||
      !this.isDissociableState
    )
      return;
    this.selectHubDevice = checked;
    if (checked) {
      // Hub selection supersedes individual aux — clear aux selections and disable their checkboxes
      this.auxCameras.forEach((c) => (c.selected = false));
    }
  }

  onAuxCheckChange(camera: AuxCameraView, checked: boolean): void {
    if (
      this.hubDissociationPending ||
      this.selectHubDevice ||
      !this.canDissociate
    )
      return;
    camera.selected = checked;
  }

  // Delegates to isHubFresh so the hub banner and the Request New Image button
  // use exactly the same 24h boundary as the tiles. Previously this used <=,
  // which called an exactly-24h-old hub "connected" while the tiles it sits
  // above rendered No Signal.
  isHubConnected(): boolean {
    return this.isHubFresh;
  }

  // LX1 hub uses a longer capture window; AM-prefixed hub serials are the older model.
  get isLytxOneHub(): boolean {
    const hub = this.hubSerial.toUpperCase();
    return !!hub && !hub.startsWith('AM');
  }

  get isErOffline(): boolean {
    return HubAuxModalComponent.OFFLINE_STATES.includes(
      this.metadata?.operationalState ?? '',
    );
  }

  get canRequestNewImage(): boolean {
    return (
      this.auxCameras.length > 0 && !this.isErOffline && this.isHubConnected()
    );
  }

  // Capture date of the returned aux images (shared across a request), shown like the profile's "Image Capture".
  get imageCaptureDate(): string {
    const cam = this.auxCameras.find((c) => !!c.photo?.url && !!c.photoSavedDate);
    return cam?.photoSavedDate
      ? new Date(cam.photoSavedDate).toLocaleString()
      : '';
  }

  async onRequestNewImage(): Promise<void> {
    const views = this.auxCameras.map((c) => c.name);
    if (!views.length || this.requestingImages) return;

    this.requestingImages = true;
    this.liveImageError = null;
    this.cdr.markForCheck();

    try {
      const result = await this.liveImageService.requestNewImages(
        this.deviceSerial,
        views,
        this.isLytxOneHub,
      );
      const retrieved =
        result.status === 'COMPLETE' &&
        result.images.some((i) => !!i.signedUrl);
      if (retrieved) {
        this.applyLiveImages(result.images);
      } else {
        this.liveImageError = HubAuxModalComponent.REQUEST_FAILED_MESSAGE;
      }
    } catch {
      this.liveImageError = HubAuxModalComponent.REQUEST_FAILED_MESSAGE;
    } finally {
      this.requestingImages = false;
      this.cdr.markForCheck();
    }
  }

  private applyLiveImages(images: LiveImageResponse[]): void {
    const byName = new Map<string, LiveImageResponse>(
      images.map((img) => [img.name?.toUpperCase(), img]),
    );
    this.auxCameras.forEach((cam) => {
      const img = byName.get(cam.name.toUpperCase());
      if (!img?.signedUrl) {
        // No fresh image for this view: leave the card as-is. What renders is
        // derived from the dates by showImage/showNoPreview/showNoSignal, so
        // image presence must never flip the signal verdict either way.
        return;
      }
      cam.photo = {
        viewName: img.name,
        url: img.signedUrl,
        savedDate: img.photoSavedDate,
      };
      // Always take the date from THIS response. Keeping the previous value
      // would pair a new URL with an unrelated older timestamp, making the
      // image look displayable — and get compared against the wrong date.
      cam.photoSavedDate = HubAuxModalComponent.toIsoOrEmpty(
        img.photoSavedDate,
      );
    });
  }

  async onDisassociate(): Promise<void> {
    if (this.isDisassociateDisabled) return;

    if (this.selectHubDevice) {
      // Hub dissociation is a short, awaited backend call — an admin device-API
      // association clear for LX1 hubs, or a Unisyn settings push for AVM hubs
      // (see dissociateHub). Await it, then let the parent confirm backend
      // completion via polling.
      this.isProcessing = true;
      this.cdr.markForCheck();
      try {
        await this.dissociateHub();
        // Fire aux dissociation for every aux camera tied to this hub.
        // Previously dissociated views keep their original date; only views
        // without a date get the hub-dissociation timestamp.
        // Same fire-and-don't-block pattern as the aux-only path: the parent
        // reconciles actual state via polling, so we don't await here.
        void this.dissociateAllAuxOnHubDissociation().catch((error) =>
          console.error('Aux dissociation during hub dissociation error:', error),
        );
        this.activeModal.close('hub-dissociated');
      } catch (error) {
        console.error('Hub dissociation failed:', error);
        this.toastr.error('Failed to dissociate. Please try again.');
        this.isProcessing = false;
        this.cdr.markForCheck();
      }
      return;
    }

    // AUX dissociation is a long-running backend operation that frequently exceeds the
    // API gateway timeout (504) even when it ultimately succeeds. Don't block the modal
    // on it: fire the request, close immediately, and hand the affected view names to
    // the parent, which hides them and confirms the real state via polling.
    const selectedViewNames = this.auxCameras
      .filter((c) => c.selected)
      .map((c) => c.name);
    void this.dissociateSelectedAux(selectedViewNames).catch((error) =>
      // A 504/timeout here is expected for the long-running op; the parent reconciles
      // via polling, so swallow it rather than surfacing a false failure.
      console.error(
        'Aux dissociation request error (reconciling via polling):',
        error,
      ),
    );
    this.activeModal.close({ type: 'aux-dissociated', views: selectedViewNames });
  }

  onCancel(): void {
    this.activeModal.dismiss('cancel');
  }

  formatLastRecordedDate(date: string): string {
    if (!date) return 'No recording';
    return new Date(date).toLocaleString();
  }

  getRelativeTime(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays > 0) return `${diffDays}d ago`;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours > 0) return `${diffHours}h ago`;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    return diffMins > 0 ? `${diffMins}m ago` : '0h ago';
  }

  private checkPermissions(): void {
    const groups = this.sessionService.getCurrentSession()?.groups ?? [];
    const bypassPermission =
      !environment.production &&
      new URLSearchParams(window.location.search).get(
        'bypassDissociatePermission',
      ) === 'true';
    this.canDissociate =
      bypassPermission ||
      groups.some((g) =>
        ['DeviceMonitoringWriter', 'DeviceMonitoringAdmin'].includes(g),
      );
  }

  private buildAuxCameraList(): void {
    const auxViews = this.getAuxViews();
    this.auxCameras = auxViews.map((view) => {
      const photo = this.findPhotoForView(view.name);
      return {
        name: view.name,
        label: view.label || view.name,
        photo,
        selected: false,
        lastRecordedDate: view.lastRecordedDate || '',
        photoSavedDate: HubAuxModalComponent.toIsoOrEmpty(photo?.savedDate),
      };
    });
  }

  private getAuxViews(): any[] {
    if (!this.metadata?.views) return [];
    // Hide AUX views that have already been dissociated — i.e. that carry a
    // viewDisassociationAcknowledgedDate from the shadow record.
    return this.metadata.views.filter(
      (view: any) =>
        view.name?.toUpperCase().includes('AUX') &&
        !view.viewDisassociationAcknowledgedDate,
    );
  }

  private findPhotoForView(viewName: string): IDevicePhoto | undefined {
    return this.photos?.find(
      (p) => p.viewName?.toUpperCase() === viewName?.toUpperCase(),
    );
  }

  // Hub-level gate: a stale hub forces every aux view to No Signal. A missing
  // or unparseable date counts as stale — we cannot claim the hub is healthy
  // when it has never reported.
  private get isHubFresh(): boolean {
    const hubLastCommTime = new Date(this.hubLastCommunicated).getTime();
    if (isNaN(hubLastCommTime)) return false;
    return (
      new Date().getTime() - hubLastCommTime < HubAuxModalComponent.ONE_DAY_MS
    );
  }

  private isRecordedDateWithin24Hours(
    recordedDate: string | undefined,
  ): boolean {
    if (!recordedDate) return false;
    const recordedTime = new Date(recordedDate).getTime();
    if (isNaN(recordedTime)) return false;
    if (!this.isHubFresh) return false;
    return (
      new Date().getTime() - recordedTime < HubAuxModalComponent.ONE_DAY_MS
    );
  }

  // new Date(x).toISOString() throws "Invalid time value" on an unparseable
  // value, so parse and check before converting. An unusable date becomes '',
  // which hasDisplayablePhoto treats as "not displayable".
  private static toIsoOrEmpty(value: string | Date | null | undefined): string {
    if (!value) return '';
    const time = new Date(value).getTime();
    return isNaN(time) ? '' : new Date(time).toISOString();
  }

  // A photo is only a candidate for display when it has both a URL and a
  // parseable save date. Without a date we cannot tell whether it predates the
  // last recording, so it is not something we can show — and, importantly, it
  // must not count as "an image exists" either, or a fresh view would drop to
  // No Signal instead of the correct "No image available".
  private hasDisplayablePhoto(cam: AuxCameraView): boolean {
    return !!cam.photo?.url && !isNaN(new Date(cam.photoSavedDate).getTime());
  }

  // An image is only worth showing when it was refreshed AFTER the view's last
  // recording. A photo taken before the last recorded frame is stale by
  // definition — that is the same situation as having no signal.
  private isPhotoAheadOfRecording(cam: AuxCameraView): boolean {
    const photoTime = new Date(cam.photoSavedDate).getTime();
    const recordedTime = new Date(cam.lastRecordedDate).getTime();
    // No usable recording date means there is nothing to be ahead of, and the
    // view has no signal to report.
    if (isNaN(photoTime) || isNaN(recordedTime)) return false;
    return photoTime > recordedTime;
  }

  // Show the image only when the hub is reporting and the photo is newer than
  // the last recording. Note this deliberately does NOT require
  // lastRecordedDate to be within 24h: a freshly captured photo that is ahead
  // of the last recording is still worth showing.
  showImage(cam: AuxCameraView): boolean {
    return (
      this.hasDisplayablePhoto(cam) &&
      this.isHubFresh &&
      this.isPhotoAheadOfRecording(cam)
    );
  }

  // The view has signal but nothing displayable came back — either no image at
  // all, or one without a usable save date. Note an image that DOES have a date
  // but predates the last recording is NOT this state: it falls through to
  // No Signal, because a photo older than the last recorded frame is stale.
  showNoPreview(cam: AuxCameraView): boolean {
    return (
      !this.hasDisplayablePhoto(cam) &&
      this.isRecordedDateWithin24Hours(cam.lastRecordedDate)
    );
  }

  // Everything else renders No Signal: a stale hub, a stale or missing
  // lastRecordedDate, or an image that is behind the last recording.
  showNoSignal(cam: AuxCameraView): boolean {
    return !this.showImage(cam) && !this.showNoPreview(cam);
  }

  /**
   * Dissociates the hub, toggling on the follower serial number format:
   * - Non-AM follower serials are LX1 (non-AVM) hubs — clear the vehicle
   *   association directly on the follower serial via the admin device API
   *   (vehicleId + actorId set to empty GUID).
   * - AM-prefixed follower serials are AVM hubs — push the Unisyn follower
   *   settings change on the leader device (existing behavior).
   */
  private async dissociateHub(): Promise<void> {
    if (this.isLytxOneHub) {
      await this.adminDeviceService
        .disassociateHubDevice(this.hubSerial)
        .toPromise();
    } else {
      await this.settingsService.dissociateHub(this.deviceSerial);
    }
  }

  private async dissociateAllAuxOnHubDissociation(): Promise<void> {
    const allAuxViews = (this.metadata?.views ?? []).filter((v) =>
      v.name?.toUpperCase().includes('AUX'),
    );
    if (!allAuxViews.length) return;

    const hubDissociationDate = new Date().toISOString();
    const request: AuxDissociationRequest = {
      views: allAuxViews.map((v) => ({
        name: v.name as string,
        // Keep the original date if the aux was already dissociated; otherwise
        // stamp the hub-dissociation time so new dissociations are traceable.
        viewDisassociationAcknowledgedDate:
          (v as { viewDisassociationAcknowledgedDate?: string }).viewDisassociationAcknowledgedDate
          ?? hubDissociationDate,
      })),
    };
    await this.deviceViewService.dissociateAuxViews(this.hubSerial, request).toPromise();
  }

  private async dissociateSelectedAux(
    selectedViewNames: string[],
  ): Promise<void> {
    // Stamp each selected AUX view with the current UTC time as the moment the
    // user acknowledged the dissociation.
    const acknowledgedDate = new Date().toISOString();
    const request: AuxDissociationRequest = {
      views: selectedViewNames.map((name) => ({
        name,
        viewDisassociationAcknowledgedDate: acknowledgedDate,
      })),
    };

    // AUX views are attached to the follower (hub), so dissociate against the
    // follower serial rather than the leader device serial.
    await this.deviceViewService
      .dissociateAuxViews(this.hubSerial, request)
      .toPromise();
  }
}
